import React, { useEffect, useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { InputTextarea } from 'primereact/inputtextarea';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { toast } from 'react-toastify';
import {
    useCreateAdminNotificationMutation,
    useUpdateAdminNotificationMutation,
} from '../messaging/messagingSlice';
import { MessageDetail } from '../../utils/types';
import './messagingDialog.scss';
import VertexAI from '../vertexAI/VertexAI';
import VoiceToText from '../voiceToTextArea/voiceToText';

interface MessagingDialogProps {
    visible: boolean;
    onHide: any;
    employeeNumber: string;
    messageDetails?: MessageDetail;
    header: string;
    existingData: MessageDetail[];
}

export const MessagingDialog = (props: MessagingDialogProps) => {
    const [value, setValue] = useState<string>('');
    const [salutation, setSalutation] = useState<string>('The Empower 2.0 Team');
    const [title, setTitle] = useState<string>('');
    const [code, setCode] = useState<string>('');
    const [CreateMessaging] = useCreateAdminNotificationMutation();
    const [UpdateMessaging] = useUpdateAdminNotificationMutation();

    const messagingTokens = [
        {
            value: '<EMPLOYEE NAME>',
            text: 'Employee Name',
        },
        {
            value: '<EMPLOYEE SERIAL NUMBER>',
            text: 'Employee Serial Number',
        },
        {
            value: '<SCORECARD NAME>',
            text: 'Scorecard Name',
        },
    ];

    const handleDragStat = (e: any, item: any) => {
        e.dataTransfer.setData('item', JSON.stringify(item));
    };

    const handleDrop = (e: any, index: number) => {
        e.preventDefault();
        const droppedItem = JSON.parse(e.dataTransfer.getData('item'));
        const newItem = [...messagingTokens];
        newItem.splice(index, 0, droppedItem);
        setValue((prevText) => prevText + ' ' + droppedItem.value);
    };

    const handleDragOver = (e: any) => {
        e.preventDefault();
    };

    const validate = () => {
        if (title === '' || value === '' || code === '' || salutation === '') {
            return false;
        }
        return true;
    };

    const handleSubmit = async () => {
        if (!validate()) {
            toast.error('Please fill in all fields');
            return;
        }
        const message: MessageDetail = {
            'template_subject': title,
            'template_body': value,
            'template_salutation': salutation,
            'template_code': code,
            'create_by': props.employeeNumber,
        };
        CreateMessaging(message)
        .unwrap()
        .then((response: any) => {
            console.log(response);
            if (response.status) {
                toast.success('Message Created Successfully');
                props.onHide();
            } else {
                toast.error('Error Creating Message');
            }
        })
        .then((error: any) => {
            console.log(error);
            if (error) {
                toast.error('Error Creating Message');
            }
        });
    };

    const handleUpdate = () => {
        if (!validate()) {
            toast.error('Please fill in all fields');
            return;
        }
        const message: MessageDetail = {
            'template_subject': title,
            'template_body': value,
            'template_salutation': salutation,
            'template_code': code,
            'modify_by': props.employeeNumber,
            'notification_template_id': props.messageDetails?.notification_template_id,
        };
        UpdateMessaging(message)
        .unwrap()
        .then((response: any) => {
            if (response.status) {
                toast.success('Message Updated Successfully');
                props.onHide();
            } else {
                toast.error('Error Updating Message');
            }
        })
        .then((error: any) => {
            if (error) {
                toast.error('Error Creating Message');
            }
        });
    };

    useEffect(() => {
        if (props.messageDetails) {
            setTitle(props.messageDetails.template_subject);
            setValue(props.messageDetails.template_body);
            setSalutation(props.messageDetails.template_salutation);
            setCode(props.messageDetails.template_code);
        } else {
            setTitle('');
            setValue('');
            setCode('');
            setSalutation('The Empower 2.0 Team');
        }
    }, [props.visible]);

    const handleHide = () => {
        props.onHide();
    };

    const handleBlur = (e: any) => {
        if (
            !props.messageDetails &&
            props.existingData.some((item) => item.template_code === e.target.value)
        ) {
            toast.error('Code already exists');
            setCode('');
        }
    };

    const updateFromAI = (value: string) => {
        setValue(value);
    };

    return (
        <Dialog
            style={{ width: '50%' }}
            visible={props.visible}
            onHide={handleHide}
            header={props.header}
            modal
        >
            <div className="row">
                <div className="col-md-6 m-t-5">
                    <h6>Title</h6>
                    <InputText
                        placeholder="Title"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        className="borderRadius w-100"
                    />
                </div>
                <div className="col-md-6 m-t-5">
                    <h6>Code</h6>
                    <InputText
                        value={code}
                        placeholder="Code"
                        readOnly={!!props.messageDetails}
                        onChange={(e) => {
                            if (props.messageDetails) {
                                return;
                            }
                            setCode(e.target.value);
                        }}
                        onBlur={handleBlur}
                        className="borderRadius w-100"
                    />
                </div>
                <div className="col-md-12 m-t-5">
                    <h6>Body</h6>
                    <div className="textarea-wrapper">
                        <div className="textarea-body">
                            <div className="textarea-resizable-container">
                                <InputTextarea
                                    aria-label="body-comment"
                                    maxLength={4000}
                                    onDrop={(e) => handleDrop(e, 0)}
                                    onDragOver={handleDragOver}
                                    value={value}
                                    onChange={(e) => setValue(e.target.value)}
                                    rows={5}
                                    className="w-full"
                                />
                                <div className="textarea-icons">
                                    <VertexAI
                                        aiInputText={value}
                                        setTextAreaValue={updateFromAI}
                                        aiMaxLength={3900}
                                    />
                                    <VoiceToText transcript={value} onTranscriptChange={setValue} />
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="link-container">Empower.ttec.com</div>
                </div>
                {messagingTokens.length > 0 &&
                    messagingTokens.map((tokens: any) => {
                        return (
                            <div
                                draggable
                                onDragStart={(e) => handleDragStat(e, tokens)}
                                onDragOver={handleDragOver}
                                key={tokens.value}
                                className="col-md-4 m-top-5"
                            >
                                <p className="token-container">{tokens.text}</p>
                            </div>
                        );
                    })}
                <div className="col-md-6">
                    <h6>Salutation</h6>
                    <InputText
                        id="Salutation"
                        onChange={(e) => setSalutation(e.target.value)}
                        value={salutation}
                        aria-label="Salutation"
                        placeholder="Salutation"
                        className="borderRadius w-100"
                    />
                </div>
            </div>
            <div className="row mt-3 button-container">
                <Button
                    type="button"
                    className="btn-secondary w-auto me-2"
                    onClick={handleHide}
                    // disabled={!notice}
                    label="Cancel"
                />
                {props.messageDetails ? (
                    <Button type="button" onClick={handleUpdate} label="Update" className="btn-primary w-auto" />
                ) : (
                    <Button type="button" onClick={handleSubmit} label="Save" className="btn-primary w-auto" />
                )}
            </div>
        </Dialog>
    );
};
